import { useState } from "react";
import Router from "next/router";

import { useMe } from "../../me";
import { useDispatchFlashMessage } from "../../flashMessage";
import { acceptAuctionBid } from "../../services/auctionsService";
import { acceptMarketplaceOffer } from "../../services/marketplaceService";
import { useLaunchDarklyFlags } from "../../launchdarkly/ClientSideLDProvider";
import { MARKETPLACE_FEATURE_FLAG } from "../../launchdarkly/useLaunchDarkly";
import { Resource } from "../../api/useFetch";
import { Offer } from "../offer";
// @ts-ignore
import { offers } from "../../routes";

type FailureReason =
  | "already_accepted"
  | "offer_canceled"
  | "price_mismatch"
  | "unknown";

type AcceptResult = {
  success: boolean;
  reason?: FailureReason;
};

type AcceptOfferResult = {
  acceptOffer: (offer: Resource<Offer>) => Promise<AcceptResult>;
  isAccepting: boolean;
};

/**
 * Converts a price string to minor units (cents)
 * @param price - Price as string (e.g., "100.00")
 * @returns Price in minor units (e.g., 10000)
 */
function convertPriceToMinorUnits(price: string): number {
  return Math.round(parseFloat(price) * 100);
}

export function useAcceptOffer(): AcceptOfferResult {
  const [isAccepting, setIsAccepting] = useState(false);
  const me = useMe();
  const dispatchFlashMessage = useDispatchFlashMessage();
  const { flags, isReady } = useLaunchDarklyFlags();
  const useMarketplace = flags[MARKETPLACE_FEATURE_FLAG] === "marketplace";

  const acceptOffer = async (offer: Resource<Offer>): Promise<AcceptResult> => {
    // Basic validation
    if (!me.id || !me.roles || me.roles.length === 0) {
      dispatchFlashMessage("failure", "shared.unknownError");
      return { success: false, reason: "unknown" };
    }

    // Marketplace-specific validation
    if (useMarketplace && isReady) {
      if (!offer.attributes.currency) {
        dispatchFlashMessage("failure", "shared.unknownError");
        return { success: false, reason: "unknown" };
      }
    }

    setIsAccepting(true);

    try {
      if (useMarketplace && isReady) {
        // Call marketplace API
        // Marketplace API currently only supports prebooked offers
        // All offers are sent as "prebooked" type regardless of original booking_type
        const priceInMinorUnits = convertPriceToMinorUnits(
          offer.attributes.price,
        );
        await acceptMarketplaceOffer({
          offer_uuid: offer.id,
          currency: offer.attributes.currency,
          price: parseFloat(offer.attributes.price),
          price_minor_unit: priceInMinorUnits,
          type: "prebooked",
          user_id: me.id,
          user_roles: me.roles,
          user_lsp_id: me.lsp?.id || "",
          user_bd_id: me.lsp?.businessDistrict?.uuid || "",
        });
      } else {
        // Call existing Hades API
        const priceInCents = convertPriceToMinorUnits(offer.attributes.price);
        await acceptAuctionBid({
          action: "accept",
          offer_id: offer.id,
          price_in_cents: priceInCents,
          chauffeur_uuid: me.id,
          roles: me.roles,
        });
      }

      dispatchFlashMessage("success", "offer.accepted");

      // Redirect to offers list (refresh the page to get updated list)
      const { href, as } = offers();
      Router.push(href, as).catch(() => {
        // Silently ignore navigation abort errors (e.g., component unmount during navigation)
      });

      return { success: true };
    } catch (error) {
      // Handle errors - check for HttpResponseError or custom error with status/body
      const errorWithStatus = error as { status?: number; body?: any };
      const status = errorWithStatus.status;
      const body = errorWithStatus.body;

      let reason: FailureReason = "unknown";

      // Handle marketplace error format using HTTP status codes
      if (useMarketplace) {
        // Marketplace API returns standard HTTP status codes:
        // 409: Offer already accepted by another chauffeur
        // 422: Semantically invalid (e.g. price mismatch)
        // 404: Offer not found
        // 400/401/500: Generic errors
        if (status === 409) {
          dispatchFlashMessage("failure", "offer.failure.already_accepted");
          reason = "already_accepted";
        } else if (status === 422) {
          dispatchFlashMessage("failure", "offer.failure.price_mismatch");
          reason = "price_mismatch";
        } else if (status === 404) {
          dispatchFlashMessage("failure", "offer.failure.offer_canceled");
          reason = "offer_canceled";
        } else {
          // 400, 401, 500, or other errors
          dispatchFlashMessage("failure", "shared.unknownError");
        }
      } else {
        // Handle Hades API error format using status codes + error codes
        // Backend error codes: price_mismatch, conflicting_ride, already_accepted,
        // invalid_state, not_visible, unknown_chauffeur, unauthorized
        if (status === 409) {
          // 409: price_mismatch or conflicting_ride
          if (body?.code === "price_mismatch") {
            dispatchFlashMessage("failure", "offer.failure.price_mismatch");
            reason = "price_mismatch";
          } else {
            // conflicting_ride or other 409 errors -> treat as already_accepted
            dispatchFlashMessage("failure", "offer.failure.already_accepted");
            reason = "already_accepted";
          }
        } else if (status === 422) {
          // 422: usually already_accepted or invalid_state
          // but check for price_mismatch code (service layer may return 422 for some errors)
          if (body?.code === "price_mismatch") {
            dispatchFlashMessage("failure", "offer.failure.price_mismatch");
            reason = "price_mismatch";
          } else {
            dispatchFlashMessage("failure", "offer.failure.already_accepted");
            reason = "already_accepted";
          }
        } else if (status === 404) {
          // 404: not_visible (offer not found) or unknown_chauffeur
          if (body?.code === "unknown_chauffeur") {
            dispatchFlashMessage("failure", "shared.unknownError");
            reason = "unknown";
          } else {
            // not_visible -> offer was canceled/not available
            dispatchFlashMessage("failure", "offer.failure.offer_canceled");
            reason = "offer_canceled";
          }
        } else if (status === 401) {
          // 401: unauthorized (no valid roles)
          dispatchFlashMessage("failure", "shared.unknownError");
          reason = "unknown";
        } else {
          // 400, 500, or other errors
          dispatchFlashMessage("failure", "shared.unknownError");
        }
      }

      return { success: false, reason };
    } finally {
      setIsAccepting(false);
    }
  };

  return { acceptOffer, isAccepting };
}
